import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useGlitch } from "../context/GlitchContext";
import { spacing, borderRadius } from "../utils/tokens";
import Typography from "./Typography";
import { MagnifyingGlass, UserCircle, CaretLeft, PlusCircle } from "@phosphor-icons/react";
import wsjLogo from "../assets/logos/wsj-logo.svg";
import Divider from "./Divider";
import TickerHeader from "./TickerHeader";
import watchlistData from "./watchlists.json";

import "../pages/TakashiPlayground.css";

function Header({ page,  isModalOpen, setIsModalOpen }) {
    const { theme } = useTheme();
    const { triggerGlitch } = useGlitch();
    const navigate = useNavigate();
    const location = useLocation();
    const isCompanyPage = location.pathname.startsWith("/company/");

    const [indicatorStyle, setIndicatorStyle] = useState({ left: "0px", width: "0px" });
    const [watchlists, setWatchlists] = useState([]);
    const [activeTab, setActiveTab] = useState("Overview");
    const tabContainerRef = useRef(null); // ✅ Ref for scrolling container
    const tabRefs = useRef({});

    useEffect(() => {
            const storedWatchlists = JSON.parse(localStorage.getItem("watchlists")) || [];
            
            // ✅ Ensure both sources are included (Remove duplicates by `id`)
            const mergedWatchlists = [...watchlistData, ...storedWatchlists].reduce((acc, watchlist) => {
                if (!acc.some(w => w.id === watchlist.id)) acc.push(watchlist);
                return acc;
            }, []);
            // console.log(mergedWatchlists);
        setWatchlists(mergedWatchlists);
    }, []);

    const handleNavigate = (id) => {
        navigate(`/markets/watchlist/${id}`);
    };

    // ✅ Preserve Other Sections While Dynamically Adding Watchlists to "Markets"
    const tabOptions = {
        Today: ["Latest", "Business", "Technology", "Opinion", "U.S.", "World", "Books & Arts"],
        Markets: ["Live Overview", "Market Data", ...watchlists.map(w => w.name)], // ✅ Inject Watchlists
        MyWSJ: ["Saved Articles", "Subscriptions", "Settings"],
        Media: ["Podcasts", "Videos", "Newsletters"],
        More: ["Real Estate", "Lifestyle", "Wealth", "Autos", "Careers"],
    };

    const tabs = tabOptions[page] || tabOptions.Today;

    // ✅ Update Active Tab When URL Changes
    useEffect(() => {
        const path = location.pathname;

        if (path.includes("/markets/watchlist/overview")) setActiveTab("Live Overview");
        else if (path.includes("/markets/watchlist/live")) setActiveTab("Market Data");
        else {
            const watchlistMatch = watchlists.find(w => path.includes(`/markets/watchlist/${w.id}`));
            if (watchlistMatch) setActiveTab(watchlistMatch.name);
        }
    }, [location.pathname, watchlists]);

    useEffect(() => {
        const activeElement = tabRefs.current[activeTab];
        if (activeElement) {
            setIndicatorStyle({
                left: `${activeElement.offsetLeft}px`,
                width: `${activeElement.offsetWidth}px`,
            });
        }
    }, [activeTab]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    
        if (tab === "Live Overview") navigate("/markets/watchlist/overview");
        else if (tab === "Market Data") navigate("/markets/watchlist/live");
        else {
            const watchlist = watchlists.find(w => w.name === tab);
            if (watchlist) handleNavigate(watchlist.id);
        }
    
        scrollToTab(tab);
    };

    const scrollToTab = (tab) => {
        if (tabRefs.current[tab] && tabContainerRef.current) {
            const tabElement = tabRefs.current[tab];
            const containerElement = tabContainerRef.current;
    
            // ✅ Get tab's position relative to the container
            const tabLeft = tabElement.offsetLeft;
            const tabWidth = tabElement.offsetWidth;
            const containerWidth = containerElement.clientWidth;
            const containerScrollLeft = containerElement.scrollLeft;
    
            // ✅ Calculate new scroll position to center the tab
            const scrollTo = tabLeft - (containerWidth / 2) + (tabWidth / 2);
            
            // ✅ Smoothly scroll to the new position
            containerElement.scrollTo({
                left: scrollTo,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (activeTab) {
            scrollToTab(activeTab); // ✅ Ensure scrolling happens when the tab changes due to swipe
        }
    }, [activeTab]);  // ✅ Runs whenever `activeTab` updates
    
    
    

    return (
        <div className="header-container" style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000, backgroundColor: theme.colors.background }}>
            <div className="header-spacer" style={{ height: "53px", width: "100%", backgroundColor: theme.colors.fillPrimary }}></div>  

            <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${spacing.m}px`, backgroundColor: theme.colors.background, height: "60px"}}>

                {isCompanyPage ? (
                    <div style={{ cursor: "pointer", color: theme.colors.contentSecondary }} onClick={() => navigate(-1)}>
                        <CaretLeft size={24} />
                    </div>
                ) : (
                    <div style={{ cursor: "pointer", color: theme.colors.contentSecondary }}>
                        <MagnifyingGlass size={24} />
                    </div>
                )}

                {page === "Today" ? (
                    <img src={wsjLogo} alt="WSJ Logo" style={{ height: "18px" }} />
                ) : (
                    <Typography variant="headlineMedia" type="editorial">{page}</Typography>
                )}


                {isCompanyPage ? (
                    <div style={{ cursor: "pointer", color: theme.colors.contentSecondary }}>
                        <PlusCircle size={24} />
                    </div>
                ) : (
                    <div style={{ cursor: "pointer", color: theme.colors.contentSecondary }}>
                        <UserCircle size={24} />
                    </div>
                )}

            </header>

            
            {/* ✅ Tab Navigation */}

            {(["Today", "Markets", "MyWSJ", "Media", "More"].includes(page)) && (
                <div className="header-nav-container">
                    <nav         ref={tabContainerRef}  // ✅ Attach ref to track scrolling container
                    style={{ display: "flex", gap: spacing.l, justifyContent: "flex-start", width: "100%", padding: `0 ${spacing.xxxl}px 0 ${spacing.m}px`, overflowX: "auto", whiteSpace: "nowrap" }}>
                        {tabs.map((tab) => (
                            <div
                                key={tab}
                                ref={(el) => (tabRefs.current[tab] = el)}
                                style={{
                                    color: theme.colors.contentSecondary,
                                    cursor: "pointer",
                                    padding: `12px ${spacing.xxs}px`,
                                    fontWeight: activeTab === tab ? "700" : "500",
                                    color: activeTab === tab ? theme.colors.contentPrimary : theme.colors.contentSecondary
                                }}
                                onClick={() => handleTabClick(tab)}

                                // onClick={() => {
                                //     setActiveTab(tab);
                                //     if (tab === "Overview") navigate("/markets/watchlist/overview");
                                //     else if (tab === "Live Markets") navigate("/markets/watchlist/live");
                                //     else {
                                //         const watchlist = watchlists.find(w => w.name === tab);
                                //         if (watchlist) handleNavigate(watchlist.id);
                                //     }
                                // }}
                            >
                                <Typography variant="button" type="utility">{tab}</Typography>
                            </div>
                        ))}
                        {/* ✅ Active Indicator */}
                        <div style={{ ...indicatorStyle, position: "absolute", bottom: "0", height: "2px", backgroundColor: theme.colors.contentPrimary, borderRadius: "0px", zIndex: 2000, transition: "left 0.3s ease, width 0.3s ease" }} />
                        
                    </nav>

                    {/* ✅ Plus Circle Icon (Fixed to Right) */}
                    {page === "Markets" && (
                            <div 
                                style={{ 
                                    position: "absolute", 
                                    right: 0, // Keep the icon at the right end
                                    bottom: 0,
                                    padding: `${spacing.s}px ${spacing.m}px`,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    color: theme.colors.contentSecondary,
                                    background: theme.colors.fillPrimary,
                                }}
                                onClick={() => setIsModalOpen(true)} 
                            >
                                <PlusCircle size={24} />
                            </div>
                        )}
                
                    <Divider />
                </div>
            )}


            {/* ✅ Ticker Header */}
            {page === "Today" && (
                <div 
                    style={{ width: "100%" }}
                >
                    <TickerHeader />
                </div>
            )}


        </div>
    );
}

export default Header;
